/**
 * Stable cache keys for DealLens thread routes and list rows.
 */

import { stripDeallensIdPrefix, validateDeallensIdToken } from "./deallensIdUtils";

/**
 * @param {string | undefined} rawUrlParam
 * @returns {string}
 */
export function normalizeDeallensIdFromUrl(rawUrlParam) {
  if (typeof rawUrlParam !== "string" || !rawUrlParam.trim()) return "";
  const token = stripDeallensIdPrefix(rawUrlParam);
  return validateDeallensIdToken(token) ? token : "";
}

/**
 * @param {string | undefined} segment - Route param (may still be URI-encoded)
 * @returns {string}
 */
export function normalizeDealLensRouteSegment(segment) {
  const t = String(segment ?? "").trim();
  if (!t) return "";
  if (!t.includes("%")) return t;
  try {
    return decodeURIComponent(t);
  } catch {
    return t;
  }
}

function normEmail(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function tryDecodeRouteSegment(segment) {
  return normalizeDealLensRouteSegment(segment);
}

/**
 * Canonical conversation identity for thread-tail cache (property + sorted participants).
 *
 * @param {string | undefined} propertyId
 * @param {string | undefined} sender
 * @param {string | undefined} receiver
 * @returns {string}
 */
export function buildThreadKey(propertyId, sender, receiver) {
  const pid = String(propertyId ?? "").trim();
  const s = normEmail(sender);
  const r = normEmail(receiver);
  if (!pid || !s || !r) return "";
  const participants = [s, r].sort();
  return `${pid}::${participants.join("|")}`;
}

/**
 * @param {object | null | undefined} deal
 * @returns {string}
 */
export function buildThreadKeyFromDeal(deal) {
  if (!deal || typeof deal !== "object") return "";
  return buildThreadKey(deal.property_id, deal.sender, deal.receiver);
}

/**
 * Provisional key for cold-link routes before fetch resolves participant emails.
 *
 * @param {string | undefined} rawUrlParam
 * @param {string | undefined} routeSenderId
 * @param {string | undefined} routeReceiverId
 * @returns {string}
 */
export function buildProvisionalRouteThreadKey(rawUrlParam, routeSenderId, routeReceiverId) {
  const urlIdToken = normalizeDeallensIdFromUrl(rawUrlParam);
  const s = normEmail(tryDecodeRouteSegment(routeSenderId));
  const r = normEmail(tryDecodeRouteSegment(routeReceiverId));
  if (!urlIdToken || !s || !r) return "";
  return `route:${urlIdToken}|${s}|${r}`;
}

/**
 * Property-offers multi-lineage tail key (separate from Opportunities hub).
 *
 * @param {string} threadKey
 * @param {string | null | undefined} collectionId
 * @returns {string}
 */
export function buildCollectionTailKey(threadKey, collectionId) {
  const base = String(threadKey ?? "").trim();
  if (!base) return "";
  const coll = String(collectionId ?? "").trim();
  return coll ? `${base}::${coll}` : base;
}

/**
 * Stable cache key for a DealLens thread route or list row (legacy tip-id key).
 *
 * @param {string | undefined} rawUrlParam
 * @param {string | undefined} routeSenderId
 * @param {string | undefined} routeReceiverId
 * @returns {string}
 */
export function buildDealLensThreadCacheKey(rawUrlParam, routeSenderId, routeReceiverId) {
  const urlIdToken = normalizeDeallensIdFromUrl(rawUrlParam);
  const s = normEmail(tryDecodeRouteSegment(routeSenderId));
  const r = normEmail(tryDecodeRouteSegment(routeReceiverId));
  if (!urlIdToken || !s || !r) return "";
  return `${urlIdToken}|${s}|${r}`;
}

/**
 * @param {object | null | undefined} deal
 * @returns {string}
 */
export function buildDealLensThreadCacheKeyFromDeal(deal) {
  if (!deal || typeof deal !== "object") return "";
  const rawId = String(deal.id ?? deal.deallens_id ?? "").trim();
  return buildDealLensThreadCacheKey(rawId, deal.sender, deal.receiver);
}

/**
 * Resolves the best available thread-tail cache key for a deal or route.
 *
 * @param {object | null | undefined} deal
 * @param {string | undefined} [rawUrlParam]
 * @param {string | undefined} [routeSenderId]
 * @param {string | undefined} [routeReceiverId]
 * @returns {string}
 */
export function resolveThreadTailKey(deal, rawUrlParam, routeSenderId, routeReceiverId) {
  const fromDeal = buildThreadKeyFromDeal(deal);
  if (fromDeal) return fromDeal;
  return buildProvisionalRouteThreadKey(rawUrlParam, routeSenderId, routeReceiverId);
}

/**
 * @param {string | undefined} value
 * @returns {string}
 */
export function normalizeDealLensThreadEmail(value) {
  return normEmail(value);
}

/**
 * @param {object | null | undefined} deal
 * @returns {boolean}
 */
export function dealMatchesThreadKey(deal, threadKey) {
  if (!deal || !threadKey) return false;
  return buildThreadKeyFromDeal(deal) === threadKey;
}
